/* eslint-disable warp-drive/no-legacy-request-patterns */
import PostType from '@linc-technologies/linced-common/enums/post-type';
import type Media from '@linc-technologies/linced-common/models/media';
import type Post from '@linc-technologies/linced-common/models/posts/post';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

// The media relationship of each post type that is not a normal post. It is
// the inverse of the mediaIds of that type. The store does not accept a
// notice on media.postId. The media serializer sends these as postId.
export const MEDIA_POST_KEYS = new Map<number, string>([
  [PostType.COMMUNITY_NOTICE, 'communityNoticeId'],
  [PostType.STUDENT_NOTICE, 'studentNoticeId'],
  [PostType.STAFF_NOTICE, 'staffNoticeId'],
  [PostType.GLOBAL_NOTICE, 'globalNoticeId'],
  [PostType.TEXT, 'textId'],
  [PostType.ATTENDANCE_NOTE, 'attendanceNoteId'],
]);

// mediaPostKey gives the media relationship that links media to the post.
export function mediaPostKey(post: Post) {
  return MEDIA_POST_KEYS.get(post.postType) ?? 'postId';
}

// saveWithMedia saves a new post, then its media (which needs the post's id),
// then the post again so it lists them. A media record that fails to save is
// unloaded first, so the post isn't re-saved with an unsaved (null) media id.
// Once the post is created it only rejects if that first save failed: a
// failed media save or re-save calls onMediaError instead, since the post
// exists and saving it again from scratch would create a second one. If the
// re-save fails, the media that saved is deleted rather than left pointing at
// a post that doesn't list it, so the user can add it again by editing.
export async function saveWithMedia(
  post: Post,
  media: Media[],
  onMediaError: () => void,
) {
  await post.save();
  if (!media.length) {
    return;
  }
  const results = await Promise.allSettled(
    // An async function, so that a throw from set rejects only this record.
    media.map(async (record) => {
      record.set(mediaPostKey(post), post);
      return record.save();
    }),
  );

  const saved: Media[] = [];
  results.forEach((result, i) => {
    if (result.status === 'rejected') {
      // results line up one-to-one with media.
      rollbackRecord(media[i] as Media);
    } else {
      saved.push(media[i] as Media);
    }
  });

  let mediaFailed = saved.length < media.length;
  try {
    await post.save();
  } catch {
    // A failed delete leaves that record as it was; there's nothing more to
    // try here, and the user is told to re-add the attachments either way.
    await Promise.allSettled(saved.map((record) => record.destroyRecord()));
    rollbackRecord(post);
    mediaFailed = true;
  }
  if (mediaFailed) {
    onMediaError();
  }
}
